// Cascara del portal del proveedor (PP-62, rediseño 2026-10-08): barra
// lateral colapsable con las secciones, cabecera de una linea con el filtro
// de planta global y el menu de cuenta, y abajo la seccion que pide la URL.
// El portal abre en el trabajo, no en un selector de plantas.
//
// El shell no sabe que hay en cada seccion: las recibe por props
// (shell/types.ts). Tampoco decide el tenant: el filtro es una preferencia de
// vista validada contra las habilitaciones de la cuenta, y la API vuelve a
// validar cada request (ADR-015).
//
// Mientras las habilitaciones no llegaron, o si fallaron, ninguna seccion se
// pinta: sin plantas no hay nada correcto que mostrar. Lo mismo el `frame`
// (types.ts), que envuelve la seccion activa y no se remonta al cambiarla.
//
// La cabecera y la barra lateral reciben dos enchufes, igual que el frame:
// `headerStatus` (lo que va junto al filtro de planta) y `navBadges` (los
// contadores de la barra). El shell les pasa el alcance (plantas, filtro,
// conexion) y los pinta solo con plantas; que dicen lo decide el portal.
//
// Sin conexion (navigator.onLine): una banda debajo de la cabecera lo dice
// una sola vez para todo el portal; cada seccion sigue mostrando lo ultimo
// recibido y reintentando sola, como antes.
import { useCallback, useEffect, useState } from "react";
import type { PortalSession } from "../auth/session.js";
import { Alert } from "../components/ui/alert.js";
import { cn } from "../components/ui/cn.js";
import { Icon } from "../components/ui/icon.js";
import { Select } from "../components/ui/input.js";
import { effectiveSiteId, hrefFor, showSiteFilter, siteOptions, toggledNav, type NavPreference } from "../logic/navigation.js";
import type { EstadoDePlantas } from "../logic/sites.js";
import type { AvisoDeOfertas } from "../notifications/useOfferMail.js";
import { AccountMenu } from "./AccountMenu.js";
import { readNavPreference, writeNavPreference } from "./navPreference.js";
import { Sidebar } from "./Sidebar.js";
import type { HeaderStatus, NavBadges, PortalSection, SectionContext, SectionFrame, ShellScope } from "./types.js";
import { useRoute } from "./useRoute.js";

interface Props {
  session: PortalSession;
  plantas: EstadoDePlantas;
  sections: readonly PortalSection[];
  onLogout: () => void;
  /** Aviso de oferta por mail (PP-68), en el menu de cuenta. */
  aviso?: AvisoDeOfertas;
  onCambiarAviso?: (activo: boolean) => void;
  /** Aviso sonoro de oferta nueva (PP-71), en el menu de cuenta: `true` es encendido. */
  sonido?: boolean;
  onCambiarSonido?: (encendido: boolean) => void;
  /** Envoltorio persistente de la seccion activa (types.ts). */
  frame?: SectionFrame;
  /** Estado de la cabecera junto al filtro (types.ts). Desde 1024px: mas angosto no entra con el filtro y la cuenta. */
  headerStatus?: HeaderStatus;
  /** Contadores de la barra lateral por ruta (types.ts). */
  navBadges?: NavBadges;
}

/** Ancho desde el que la barra sin preferencia se ve ancha (xl de Tailwind). */
const WIDE_VIEWPORT_PX = 1280;

/** Click comun: los modificados (nueva pestana, ventana) quedan en manos del navegador. */
function plainClick(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function framed(frame: SectionFrame | undefined, context: SectionContext, active: PortalSection): React.ReactNode {
  const section = (
    <div key={active.path} className="contents">
      {active.render(context)}
    </div>
  );
  return frame === undefined ? section : frame(context, section);
}

/** `navigator.onLine` y sus eventos: suficiente para avisar; las lecturas siguen decidiendo por su cuenta. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function PortalShell({ session, plantas, sections, onLogout, aviso, onCambiarAviso, sonido, onCambiarSonido, frame, headerStatus, navBadges }: Props): React.JSX.Element {
  const { route, view, goTo, setSite, setView } = useRoute(sections.map((section) => section.path));
  const sites = plantas.estado === "listo" ? plantas.plantas : [];
  const siteId = effectiveSiteId(sites, route.siteId);
  const active = sections.find((section) => section.path === route.path) ?? sections[0];
  const online = useOnline();

  const [navPreference, setNavPreference] = useState<NavPreference | null>(() => readNavPreference());
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Una planta de la URL que la cuenta no tiene se saca de la URL en cuanto
  // se conocen las habilitaciones: el filtro queda en "Todas", sin error.
  const loaded = plantas.estado === "listo";
  const stale = loaded && route.siteId !== null && !sites.some((site) => site.siteId === route.siteId);
  useEffect(() => {
    if (stale) setSite(null);
  }, [stale, setSite]);

  // Escape cierra el cajon del celular y devuelve el foco al boton de menu.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setDrawerOpen(false);
      document.getElementById("portal-menu")?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const toggleNav = useCallback(() => {
    const next = toggledNav(navPreference, window.innerWidth >= WIDE_VIEWPORT_PX);
    setNavPreference(next);
    writeNavPreference(next);
  }, [navPreference]);

  const navigate = useCallback(
    (path: string, event: React.MouseEvent) => {
      if (!plainClick(event)) return;
      event.preventDefault();
      setDrawerOpen(false);
      goTo(path);
    },
    [goTo],
  );

  // Sin plantas no hay alcance: ni contadores ni estado en la cabecera.
  const scope: ShellScope | null = loaded && sites.length > 0 ? { sites, siteId, online } : null;
  const items = sections.map((section) => {
    const badge = scope === null || navBadges === undefined ? null : navBadges(section.path, scope);
    return {
      path: section.path,
      label: section.label,
      href: hrefFor({ path: section.path, siteId: route.siteId }),
      ...(section.icon === undefined ? {} : { icon: section.icon }),
      ...(badge === null || badge === undefined ? {} : { badge }),
    };
  });
  const siteName = siteId === null ? null : (siteOptions(sites).find((option) => option.siteId === siteId)?.name ?? null);

  return (
    <div className="flex min-h-full">
      <Sidebar
        items={items}
        activePath={active?.path}
        preference={navPreference}
        drawerOpen={drawerOpen}
        onNavigate={navigate}
        onToggle={toggleNav}
        onCloseDrawer={() => setDrawerOpen(false)}
      />

      <div className="flex min-h-full min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-divisor bg-pagina/85 backdrop-blur-sm">
          <div className="flex h-cab items-center gap-3 px-3 md:px-5">
            <button
              id="portal-menu"
              type="button"
              aria-label="Abrir el menú"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
              className="grid size-9 shrink-0 place-items-center rounded-md text-texto-suave hover:bg-velo md:hidden"
            >
              <Icon name="menu" className="size-5" />
            </button>

            <div className="flex min-w-0 flex-col leading-tight">
              <p className="m-0 truncate text-base font-semibold text-blanco">{active?.label ?? "Portal del proveedor"}</p>
              <span className="hidden truncate text-eyebrow text-texto-suave md:block">
                {siteName ?? (sites.length > 0 ? "Todas las plantas de la empresa" : "Portal del proveedor")}
              </span>
            </div>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              {scope !== null && headerStatus !== undefined && (
                <div className="hidden items-center gap-3 lg:flex">{headerStatus(scope)}</div>
              )}
              {showSiteFilter(sites) && (
                <label className="flex items-center gap-2 text-xs text-texto-suave">
                  <span className="hidden sm:inline">Planta</span>
                  <Select
                    aria-label="Planta"
                    value={siteId ?? ""}
                    onChange={(event) => setSite(event.target.value === "" ? null : event.target.value)}
                    className="h-8 w-auto max-w-[200px] py-0 text-sm"
                  >
                    <option value="">Todas</option>
                    {siteOptions(sites).map((option) => (
                      <option key={option.siteId} value={option.siteId}>
                        {option.name}
                      </option>
                    ))}
                  </Select>
                </label>
              )}
              <AccountMenu
                user={session.user}
                plantas={plantas}
                onLogout={onLogout}
                {...(aviso === undefined ? {} : { aviso })}
                {...(onCambiarAviso === undefined ? {} : { onCambiarAviso })}
                {...(sonido === undefined ? {} : { sonido })}
                {...(onCambiarSonido === undefined ? {} : { onCambiarSonido })}
              />
            </div>
          </div>
          {!online && (
            <p role="status" className="m-0 flex flex-wrap items-center gap-x-2 border-t border-ambar/35 bg-ambar/12 px-4 py-2 text-xs md:px-5">
              <span className="inline-flex items-center gap-2 font-medium text-ambar">
                <Icon name="sinConexion" className="size-3.5" />
                Sin conexión con UCORP.
              </span>
              <span className="text-texto-2">Se muestra lo último recibido y se reintenta sola al volver la conexión.</span>
            </p>
          )}
        </header>

        <main className={cn("mx-auto flex w-full flex-1 flex-col gap-4 p-3 md:p-5", active?.wide === true ? "" : "max-w-[1180px]")}>
          {plantas.estado === "cargando" && (
            <div aria-busy="true" className="flex flex-col gap-4">
              <p role="status" className="sr-only">Consultando las plantas habilitadas...</p>
              <div aria-hidden="true" className="hueso h-16 w-full" />
              <div aria-hidden="true" className="hueso h-40 w-full" />
            </div>
          )}

          {plantas.estado === "denegado" && (
            <Alert variant="error">
              La sesión no tiene acceso al portal. Salir e ingresar con una cuenta de operador de proveedor.
            </Alert>
          )}

          {plantas.estado === "error" && (
            <Alert variant="error">No se pudo consultar la lista de plantas. Reintentar en unos minutos.</Alert>
          )}

          {plantas.estado === "listo" && sites.length === 0 && (
            <Alert variant="info">
              La cuenta está activa y todavía no tiene habilitación en ninguna planta. Cuando UCORP habilite a la
              empresa, la planta aparece acá sin ninguna acción adicional.
            </Alert>
          )}

          {plantas.estado === "listo" && sites.length > 0 && active !== undefined && framed(frame, { session, sites, siteId, selectSite: setSite, view, selectView: setView }, active)}
        </main>

        <footer className="flex flex-col gap-1 border-t border-divisor px-4 py-2.5 text-eyebrow text-texto-debil md:flex-row md:justify-between md:px-5">
          <span>UCORP · Portal del proveedor · staging</span>
          <span>Horarios en la zona de cada planta</span>
        </footer>
      </div>
    </div>
  );
}
